import { action, observable } from 'mobx';
import { timestampNow } from '@bufbuild/protobuf/wkt';
import { PagedCollection, type PagedModelSelectOptions } from 'core/model/PagedCollection';
import {
  CorrelationAckState,
  CorrelationStatus
} from '@kentik/schema/gen/ts/kentik/alerting/correlation/v202609/correlation_pb';
import { SortOrder } from '@kentik/schema/gen/ts/kentik/alerting/types/v202303/types_pb';
import $auth from 'app/stores/$auth';
import {
  bulkAckCorrelations,
  bulkCloseCorrelations,
  bulkUnAckCorrelations,
  listCorrelations,
  mergeCorrelations,
  type BulkActionResult
} from './correlationsApi';
import { toFilter } from './filters';
import CorrelationModel from './CorrelationModel';
import { CONFIDENCE_BAND_ORDER, GROUP_ORDER } from './constants';
import type { ConfidenceBand, CorrelationFilterValues } from './types';

export default class CorrelationCollection extends PagedCollection<CorrelationModel> {
  @observable.ref
  public serverFilter: CorrelationFilterValues = {};

  /** Only the latest request's response is applied. */
  private fetchSequence = 0;

  /** Counted apart from `requestStatus`, so a refresh during a bulk action doesn't end either one early. */
  @observable
  private pendingUpdates = 0;

  /** Merged-away correlations stay closed across reloads until the server catches up. */
  private pendingClosedIds = new Set<string>();

  get model() {
    return CorrelationModel;
  }

  public get sortedGroupKeys(): string[] {
    const order = typeof this.groupBy === 'string' ? GROUP_ORDER[this.groupBy] : undefined;

    if (!order) {
      return super.sortedGroupKeys;
    }

    return Object.keys(this.groupedData).sort((a, b) => {
      const aIndex = order.indexOf(a);
      const bIndex = order.indexOf(b);
      if (aIndex === bIndex) {
        return a.localeCompare(b);
      }
      return (aIndex === -1 ? order.length : aIndex) - (bIndex === -1 ? order.length : bIndex);
    });
  }

  public serverSortFn(): Promise<null> {
    this.setPage(1);
    return this.fetch();
  }

  /** A refresh keeps the page and the selection; anything else starts over on page 1. */
  @action
  setServerFilter(filter: CorrelationFilterValues, { refresh = false }: { refresh?: boolean } = {}): Promise<null> {
    this.serverFilter = filter;
    if (!refresh) {
      this.setPage(1);
    }
    return this.fetch({ preserveSelection: refresh });
  }

  public isRequestActive(this: this, label?: string): boolean {
    if (label === 'updating') {
      return this.pendingUpdates > 0;
    }
    return super.isRequestActive(label) || (!label && this.pendingUpdates > 0);
  }

  /** The base multi-page selectAll doesn't apply to a collection holding a single server page. */
  public selectAll(_options: PagedModelSelectOptions = {}): void {
    this.selected = this.isAllSelected ? null : this.models.filter((model) => model.isSelectable);
  }

  groupSummary(group: CorrelationModel[]): {
    alertCount: number;
    commonConfidenceBand: ConfidenceBand;
    oldestDurationMs: number;
  } {
    const bandCount = (band: ConfidenceBand) =>
      group.filter((correlation) => correlation.confidenceBand === band).length;

    return {
      alertCount: group.reduce((total, correlation) => total + correlation.get('totalAlertCount'), 0),
      commonConfidenceBand: CONFIDENCE_BAND_ORDER.reduce((best, band) =>
        bandCount(band) > bandCount(best) ? band : best
      ),
      oldestDurationMs: Math.max(0, ...group.map((correlation) => correlation.durationMs()))
    };
  }

  @action
  async fetch({ preserveSelection = false }: { preserveSelection?: boolean } = {}): Promise<null> {
    const { offset, perPage } = this.pageState;
    const { field, direction } = this.sortState;
    this.fetchSequence += 1;
    const sequence = this.fetchSequence;

    this.setRequestStatus('fetching');
    this.hasFetched = true;
    try {
      const response = await listCorrelations({
        filter: toFilter(this.serverFilter),
        pagination: { limit: BigInt(perPage), offset: BigInt(offset), includeTotalCount: false },
        sorting: field
          ? { fields: [{ name: field, order: direction === 'asc' ? SortOrder.ASCENDING : SortOrder.DESCENDING }] }
          : undefined
      });
      if (sequence === this.fetchSequence) {
        const selectedIds = new Set(preserveSelection ? this.selectedArray.map((model) => model.id) : []);
        this.clearSelection();
        this.set(response.correlations);
        // Selection is by model instance, so carry it over to the refetched models by id.
        const reselected = this.models.filter((model) => selectedIds.has(model.id));
        this.selected = reselected.length > 0 ? reselected : null;
        this.applyPendingClosed();
        this.hasMoreResults = response.pagination?.hasMoreResults ?? false;
        this.lastFetched = Date.now();
        this.error = undefined;
      }
      return null;
    } catch (error) {
      if (sequence === this.fetchSequence) {
        this.error = { label: 'fetching', body: error };
      }
      throw error;
    } finally {
      if (sequence === this.fetchSequence) {
        this.setRequestStatus(null);
      }
    }
  }

  @action
  private applyPendingClosed(): void {
    this.pendingClosedIds.forEach((id) => {
      const model = this.get(id);
      if (model?.isOpen) {
        model.set({ status: CorrelationStatus.CLOSED });
      } else if (model) {
        this.pendingClosedIds.delete(id);
      }
    });
  }

  @action
  markClosed(ids: string[], { pending = false } = {}): void {
    ids.forEach((id) => {
      this.get(id)?.set({ status: CorrelationStatus.CLOSED });
      if (pending) {
        this.pendingClosedIds.add(id);
      }
    });
  }

  async merge({ sourceId, targetId }: { sourceId: string; targetId: string }): Promise<void> {
    await this.updating(() => mergeCorrelations({ sourceId, targetId }));
    this.markClosed([sourceId], { pending: true });
  }

  /** Bulk actions report as `updating`, so the list can disable further actions until they finish. */
  private async updating<T>(request: () => Promise<T>): Promise<T> {
    this.setPendingUpdates(1);
    try {
      return await request();
    } finally {
      this.setPendingUpdates(-1);
    }
  }

  @action
  private setPendingUpdates(delta: number): void {
    this.pendingUpdates += delta;
  }

  async bulkAck(ids: string[]): Promise<BulkActionResult> {
    const result = await this.updating(() => bulkAckCorrelations(ids));
    const ackedByUserId = BigInt($auth.activeUser?.id ?? 0);
    const ackedAt = timestampNow();
    result.appliedIds.forEach((id) =>
      this.get(id)?.set({ ackState: CorrelationAckState.ACKED, ackedByUserId, ackedAt })
    );
    return result;
  }

  async bulkUnAck(ids: string[]): Promise<BulkActionResult> {
    const result = await this.updating(() => bulkUnAckCorrelations(ids));
    result.appliedIds.forEach((id) =>
      this.get(id)?.set({ ackState: CorrelationAckState.NOT_ACKED, ackedByUserId: BigInt(0), ackedAt: undefined })
    );
    return result;
  }

  async bulkClose(ids: string[]): Promise<BulkActionResult> {
    const result = await this.updating(() => bulkCloseCorrelations(ids));
    this.markClosed(result.appliedIds, { pending: true });
    return result;
  }
}
